import { Outlet, ReactLocation, Router } from "@tanstack/react-location"
import { useAuth } from "auth"
import { BonzaiProvider } from "bonzai"
import { EnvironmentContext } from "graphql-mock"
import { DialogContainer, Toaster, TooltipContainer } from "materia"
import { APP_CONFIG } from "optimizer-core"
import { useState } from "react"
import { OptimizeContextProvider } from "ui-tools"
import { ErrorPopup, QueryBoundary } from "utility-components"
import { parseSearch } from "utils"
import { ChatProvider } from "./chat/ChatProvider"
import { MessageNotifier } from "./chat/components/MessageNotifier"
import { AIChatContextProvider } from "./components/AIChat/providers/AIChatProvider"
import { AppShellConnected } from "./components/AppShell"
import { CurrentCompanyProvider } from "./components/AppShell/CurrentCompanyProvider"
import { CurrentUserProvider } from "./components/AppShell/CurrentUserProvider"
import { ThemeProvider } from "./components/AppShell/ThemeProvider"
import { ConfettiContextProvider } from "./components/ConfettiBanner/ConfettiContextProvider"
import { DiplomaUnlockModalConnected } from "./components/DiplomaUnlockModal"
import { SharedDeviceInactivityTrackerConnected } from "./components/SharedDeviceInactivityTracker"
import { GRAPHQL_ENVIRONMENT } from "./constants"
import { OPTIMIZER_URL } from "./env"
import { getRoutes } from "./routes"
import { usePageHistoryTracker } from "./tools/useNavigateBack"
import type { LocationGenerics } from "./tools/useRouteMetadata"

const APP = APP_CONFIG.find((app) => app.name === "portal")

export const App = () => (
  <BonzaiProvider>
    <EnvironmentContext.Provider value={GRAPHQL_ENVIRONMENT}>
      <OptimizeContextProvider value={{ app: APP, optimizeUrl: OPTIMIZER_URL }}>
        <TooltipContainer>
          <QueryBoundary>
            <CurrentUserProvider>
              <CurrentCompanyProvider>
                <ConfettiContextProvider>
                  <ThemeProvider>
                    <DialogContainer>
                      <AIChatContextProvider>
                        <ChatProvider>
                          <AppRouter />
                        </ChatProvider>
                      </AIChatContextProvider>
                    </DialogContainer>
                  </ThemeProvider>
                </ConfettiContextProvider>
              </CurrentCompanyProvider>
            </CurrentUserProvider>
          </QueryBoundary>
        </TooltipContainer>
      </OptimizeContextProvider>
    </EnvironmentContext.Provider>
  </BonzaiProvider>
)

const AppRouter = () => {
  const { activeSession, isPending, error } = useAuth()

  const [location] = useState(
    () => new ReactLocation<LocationGenerics>({ parseSearch })
  )

  if (isPending) return
  if (error) return <ErrorPopup error={error} />

  // TODO https://jira.attensi.com/browse/WEB-19655
  // Move away from deprecated react-location
  return (
    <Router
      routes={getRoutes()}
      location={location}
      basepath={activeSession.subdomain}
    >
      <PageHistoryTracker />

      <AppShellConnected>
        <Outlet />

        <Toaster />

        <DiplomaUnlockModalConnected />

        <SharedDeviceInactivityTrackerConnected />

        {/* Inside the router, so a notification click can route to the chat. */}
        <MessageNotifier />
      </AppShellConnected>
    </Router>
  )
}

/**
 * Records the pages the user visits so the Bundle and Event back buttons have somewhere to return
 * to. Renders nothing, and has to live inside the Router to see route matches.
 */
const PageHistoryTracker = () => {
  usePageHistoryTracker()
  return null
}
